import { FormControl, FormHelperText, FormLabel, Grid, TextField } from '@mui/material'
import * as React from 'react'
import { Controller, ControllerProps, FieldValues } from 'react-hook-form'

interface FiscalYearEndInputProps {
  value?: string
  onChange?: (value: string) => void
  error?: boolean
  helperText?: string
  label?: string
  required?: boolean
  disabled?: boolean
}

/**
 * Fiscal year end input component that renders 2 separate text inputs (month, day)
 * but manages a single YYYY-MM-DD format string value internally.
 * Uses last year since only month and day are relevant for fiscal year end.
 */
const FiscalYearEndInput = ({
  value = '',
  onChange,
  error = false,
  helperText,
  label = 'Fiscal Year End',
  required = false,
  disabled = false,
}: FiscalYearEndInputProps) => {
  // Initialize internal state from the incoming value
  const [month, setMonth] = React.useState(() => {
    if (!value) return ''
    const parts = value.split('-')
    return parts.length === 3 ? parts[1] : '' // Extract month from YYYY-MM-DD
  })

  const [day, setDay] = React.useState(() => {
    if (!value) return ''
    const parts = value.split('-')
    return parts.length === 3 ? parts[2] : '' // Extract day from YYYY-MM-DD
  })

  const handleInputChange = (newMonth: string, newDay: string) => {
    if (newMonth && newDay) {
      // Only call onChange with complete YYYY-MM-DD format with zero-padded values
      const lastYear = new Date().getFullYear() - 1
      onChange?.(`${lastYear}-${newMonth.padStart(2, '0')}-${newDay.padStart(2, '0')}`)
    } else {
      // Call onChange with empty string for incomplete values
      onChange?.('')
    }
  }

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth)
    handleInputChange(newMonth, day || '')
  }

  const handleDayChange = (newDay: string) => {
    setDay(newDay)
    handleInputChange(month || '', newDay)
  }

  return (
    <FormControl error={error} required={required} disabled={disabled} fullWidth>
      {label && <FormLabel>{label}</FormLabel>}
      <Grid container spacing={1} alignItems="center" mb={1}>
        <Grid size={3}>
          <TextField
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
            placeholder="MM"
            slotProps={{
              htmlInput: {
                maxLength: 2,
                pattern: '[0-9]*',
              },
            }}
            error={error}
            disabled={disabled}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid size={3}>
          <TextField
            value={day}
            onChange={(e) => handleDayChange(e.target.value)}
            placeholder="DD"
            slotProps={{
              htmlInput: {
                maxLength: 2,
                pattern: '[0-9]*',
              },
            }}
            error={error}
            disabled={disabled}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  )
}

type FiscalYearEndInputControllerProps<T extends FieldValues> = FiscalYearEndInputProps &
  Pick<ControllerProps<T>, 'name' | 'control' | 'rules'>

export const FiscalYearEndInputController = <T extends FieldValues>({
  name,
  control,
  rules,
  ...props
}: FiscalYearEndInputControllerProps<T>) => (
  <Controller
    render={({ field, formState: { submitCount }, fieldState: { error, isTouched } }) => {
      const isError = Boolean((isTouched || submitCount > 0) && error?.message)
      return (
        <FiscalYearEndInput
          {...props}
          {...field}
          error={isError}
          helperText={isError ? error?.message : props.helperText}
        />
      )
    }}
    name={name}
    control={control}
    rules={rules}
  />
)

export { FiscalYearEndInput }
